// ----------------------------------------------------------------------------
// New service plan wizard: When → Who's serving → Songs → Review. Creates the
// plan through plansApi.create (draft or published) and handles the plan-limit
// (402) the same way the old Add Service flow did.
// ----------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Search, X } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { availabilityApi, plansApi, songsApi } from '@app/api/organization';
import type { AvailabilityStatusValue, PlanItem, ServicePlan, Song, TeamMemberAssignment } from '@app/types';
import { Toast } from '@app/components/Notifications';
import { Banner, Button, Card, Chip, DatePicker, Field, Input, KeyBadge, Select, StatTile, Textarea, Toggle, cx } from '@app/components/ui';
import { longDate, shortDate } from '@app/lib/format';
import { redesignFlags } from '@app/featureFlags';
import { logger } from '@app/utils/logger';
import { useRosterData } from '../../hooks/useRosterData';
import { resolveCreatePlanError } from '../../utils/planErrors';
import { SERVICE_TIMES, combineDateTime, timeLabel } from '../../utils/serviceTimes';
import { PlanLimitModal } from './PlanLimitModal';
import { POSITIONS } from './TeamSlots';
import { SONG_KEYS } from '../songs/songUtils';

const STEPS = ['Service', 'Team', 'Setlist', 'Review'] as const;
const SECTIONS = ['Call to Worship', 'Praise', 'Worship', 'Response', 'Communion', 'Sending'];
const NAME_CHIPS = ['Sunday Gathering', 'Midweek Worship', 'Youth Night', 'Special service'];

interface Slot {
  id: string;
  role: string;
  userId: string;
}
interface WizSong {
  songId: string;
  title: string;
  author: string;
  key: string;
  section: string;
  hasLyrics: boolean;
  youtubeUrl?: string;
}

let seq = 0;
const nid = () => `w${Date.now()}${++seq}`;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const nextSunday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7 || 7));
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export function NewPlanWizard() {
  const { user } = useAuth();
  const { groups, selectedGroupId } = useGroup();
  const { church } = useChurch();
  const navigate = useNavigate();
  const churchId = user?.churchId;

  const [step, setStep] = useState(0);
  const [done, setDone] = useState<null | { published: boolean; plan: ServicePlan; notified: number }>(null);
  const [teamId, setTeamId] = useState<string>(selectedGroupId ?? groups[0]?.id ?? '');
  const [name, setName] = useState('Sunday Gathering');
  const [date, setDate] = useState<Date | null>(nextSunday);
  const [time, setTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [copy, setCopy] = useState<'last' | 'empty'>('last');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [songs, setSongs] = useState<WizSong[]>([]);
  const [skip, setSkip] = useState(false);
  const [library, setLibrary] = useState<Song[]>([]);
  const [query, setQuery] = useState('');
  const [availability, setAvailability] = useState<{ byUser: Record<string, AvailabilityStatusValue>; leave: Set<string> }>({ byUser: {}, leave: new Set() });
  const [submitting, setSubmitting] = useState<null | 'draft' | 'publish'>(null);
  const [toast, setToast] = useState<{ m: string; t: 'error' | 'success' } | null>(null);
  const [limit, setLimit] = useState<null | { message: string; maxPlans: number; tier: string; publish: boolean }>(null);

  useEffect(() => {
    if (!teamId && groups[0]) setTeamId(groups[0].id);
  }, [groups, teamId]);

  const { allPlans, setAllPlans, availableMembers } = useRosterData(churchId, teamId || undefined);
  const teamPlans = useMemo(() => allPlans.filter((p) => p.groupId === teamId).sort((a, b) => +new Date(a.date) - +new Date(b.date)), [allPlans, teamId]);
  const lastPlan = teamPlans.filter((p) => new Date(p.date) < new Date()).pop() ?? teamPlans[teamPlans.length - 1];
  const team = groups.find((g) => g.id === teamId);

  // Start from the team's last service unless the admin picks "empty".
  useEffect(() => {
    if (copy === 'last' && lastPlan) setSlots(lastPlan.team.filter((t) => !t.isPlaceholder).map((t) => ({ id: nid(), role: t.role, userId: t.userId })));
    if (copy === 'empty') setSlots([]);
  }, [copy, lastPlan?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!churchId) return;
    songsApi
      .getLibrary(churchId)
      .then(setLibrary)
      .catch(() => setLibrary([]));
  }, [churchId]);

  useEffect(() => {
    if (!churchId || !teamId || !date) return;
    let cancelled = false;
    availabilityApi
      .getTeam(churchId, teamId, date.getFullYear(), date.getMonth() + 1)
      .then((rows) => {
        if (cancelled) return;
        const byUser: Record<string, AvailabilityStatusValue> = {};
        const leave = new Set<string>();
        for (const r of rows) {
          if (r.onLeave) leave.add(r.userId);
          const day = r.days.find((x) => x.day === date.getDate());
          if (day && day.status !== 'Available') byUser[r.userId] = day.status;
        }
        setAvailability({ byUser, leave });
      })
      .catch(() => !cancelled && setAvailability({ byUser: {}, leave: new Set() }));
    return () => {
      cancelled = true;
    };
  }, [churchId, teamId, date]);

  const when = date ? new Date(combineDateTime(date, time)) : null;
  const conflict = when ? teamPlans.find((p) => +new Date(p.date) === +when) : undefined;
  const past = when ? when < new Date() : false;
  const memberName = (id: string) => availableMembers.find((m) => m.id === id)?.username ?? '';
  const filled = slots.filter((s) => s.userId).length;
  const open = slots.length - filled;
  const noLyrics = skip ? [] : songs.filter((s) => !s.hasLyrics).map((s) => s.title);
  const policy = church?.setlistSettings?.lyricsPublishPolicy ?? 'prompt';
  const blocked = policy === 'block' && noLyrics.length > 0;

  const memberOptions = (slotId: string) => {
    const taken = new Set(slots.filter((s) => s.id !== slotId && s.userId).map((s) => s.userId));
    return [
      { value: '', label: 'Leave unfilled', sub: 'Fill it later' },
      ...availableMembers
        .filter((m) => !taken.has(m.id))
        .map((m) => {
          const leave = availability.leave.has(m.id);
          const away = !leave && availability.byUser[m.id] === 'Blocked';
          return { value: m.id, label: m.username, disabled: leave || away, tag: leave ? 'On leave' : away ? 'Away' : undefined };
        })
        .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled) || a.label.localeCompare(b.label)),
    ];
  };

  const lib = useMemo(() => {
    const q = query.trim().toLowerCase();
    return library.filter((s) => !q || s.title.toLowerCase().includes(q) || s.author?.toLowerCase().includes(q)).slice(0, 8);
  }, [library, query]);

  const move = (i: number, d: -1 | 1) => {
    const a = [...songs];
    [a[i], a[i + d]] = [a[i + d], a[i]];
    setSongs(a);
  };

  const create = async (publish: boolean) => {
    if (!churchId || !user || !when) return;
    if (publish && blocked) {
      setToast({ m: 'Add lyrics to every song before publishing', t: 'error' });
      return;
    }
    setSubmitting(publish ? 'publish' : 'draft');
    const stamp = Date.now();
    const teamRows: TeamMemberAssignment[] = slots
      .filter((s) => s.userId)
      .map((s) => ({ id: `${s.userId}-${stamp}`, userId: s.userId, name: memberName(s.userId), role: s.role, status: 0 }));
    const items: PlanItem[] = skip
      ? []
      : songs.map((s, i) => ({ id: `item-${stamp}-${i}`, type: 'Song', title: s.title, songId: s.songId, key: s.key, section: s.section, orderIndex: i, youtubeUrl: s.youtubeUrl }));
    try {
      let plan = await plansApi.create(churchId, {
        churchId,
        groupId: teamId || undefined,
        date: when.toISOString(),
        title: name.trim() || 'Service',
        notes,
        items,
        team: teamRows,
        isPublished: publish,
        createdBy: user.id,
      });
      // Songs chosen here are the setlist; publishing the plan publishes them too.
      if (publish && items.length) plan = await plansApi.update(churchId, plan.id, { isSetlistPublished: true });
      setAllPlans((prev) => [...prev, plan]);
      setLimit(null);
      setDone({ published: publish, plan, notified: publish ? teamRows.length : 0 });
    } catch (err: any) {
      const resolved = resolveCreatePlanError(err, allPlans.length);
      if (resolved.kind === 'limit') setLimit({ message: resolved.message, maxPlans: resolved.maxPlans, tier: resolved.tier, publish });
      else setToast({ m: resolved.kind === 'message' ? resolved.message : 'Failed to create service', t: 'error' });
      logger.error('Failed to create service plan', err);
    } finally {
      setSubmitting(null);
    }
  };

  const next = () => {
    if (step === 0 && (!date || !name.trim() || !teamId)) {
      setToast({ m: 'Pick a team, a name and a date', t: 'error' });
      return;
    }
    if (step === 2 && !songs.length) setSkip(true);
    setStep(step + 1);
  };

  const reset = () => {
    setDone(null);
    setStep(0);
    setSongs([]);
    setSkip(false);
    setNotes('');
    setCopy('last');
  };

  return (
    <div className="max-w-[760px] mx-auto">
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      {!done && (
        <div className="mb-[22px]">
          <div className="flex justify-between text-[12.5px] mb-2.5">
            <span className="text-t4">New service plan · step {step + 1} of 4</span>
            <span className="text-t3 font-medium">{STEPS[step]}</span>
          </div>
          <div className="grid grid-cols-4 gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={4} aria-valuenow={step + 1} aria-label="Wizard progress">
            {STEPS.map((s, i) => (
              <div key={s} className="h-1 rounded-full bg-fill2 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: i < step ? '100%' : i === step ? '50%' : '0%', background: i < step ? 'var(--t1)' : 'var(--acc)', transition: 'width .6s var(--ease)' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <Card pad={26} className="shadow-wp">
        {done ? (
          <div className="text-center pt-3 pb-1 wp-rise">
            <div className="w-16 h-16 rounded-full bg-ok-bg text-ok flex items-center justify-center mx-auto mb-[18px]">
              <Check size={30} strokeWidth={2.2} aria-hidden="true" />
            </div>
            <h1 className="text-[26px] font-semibold tracking-[-0.025em]">{done.published ? 'Service published' : 'Draft saved'}</h1>
            <p className="text-[14.5px] text-t3 mt-2 mb-6 leading-[1.6]">
              {done.published
                ? `${done.notified} ${done.notified === 1 ? 'person was' : 'people were'} asked to confirm for ${done.plan.title} on ${longDate(done.plan.date)}.`
                : `${done.plan.title} on ${longDate(done.plan.date)} is on the ${team?.name ?? ''} roster. No one has been notified yet.`}
            </p>
            <div className="flex flex-wrap justify-center gap-2.5">
              <Button variant="ghost" shape="form" size="lg" onClick={reset}>
                Plan another
              </Button>
              <Button shape="form" size="lg" onClick={() => navigate(`/admin/roster?plan=${done.plan.id}`)}>
                View on roster
              </Button>
            </div>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="flex flex-col gap-[18px] wp-rise" key="s0">
                <div>
                  <h1 className="text-[26px] font-semibold tracking-[-0.025em]">When is the service?</h1>
                  <p className="text-[14.5px] text-t3 mt-1.5">Pick the worship team, date and time.</p>
                </div>
                <Field label="Worship team" htmlFor="wiz-team">
                  <Select
                    id="wiz-team"
                    value={teamId}
                    onChange={setTeamId}
                    options={groups.map((g) => ({ value: g.id, label: g.name, sub: `${g.memberCount} people` }))}
                  />
                </Field>
                <Field label="Service name" htmlFor="wiz-name">
                  <Input id="wiz-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sunday Gathering" />
                  <div className="flex flex-wrap gap-1.5 mt-2.5" role="group" aria-label="Suggested names">
                    {NAME_CHIPS.map((c) => (
                      <Chip key={c} size={30} on={name === c} onClick={() => setName(c)}>
                        {c}
                      </Chip>
                    ))}
                  </div>
                </Field>
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
                  <Field label="Date" htmlFor="wiz-date">
                    <DatePicker id="wiz-date" value={date} onChange={setDate} hasMarker={(d) => teamPlans.some((p) => sameDay(new Date(p.date), d))} />
                  </Field>
                  <Field label="Start time" htmlFor="wiz-time">
                    <Select
                      id="wiz-time"
                      value={time}
                      onChange={setTime}
                      options={SERVICE_TIMES.map((t) => ({
                        value: t,
                        label: timeLabel(t),
                        sub: date && teamPlans.some((p) => +new Date(p.date) === +new Date(combineDateTime(date, t))) ? 'Already has a service' : undefined,
                      }))}
                    />
                  </Field>
                </div>
                {conflict && (
                  <Banner tone="amb">
                    There is already a service at this exact time: “{conflict.title}”. Ignore this if you intend to have two services at the same time.
                  </Banner>
                )}
                {past && (
                  <Banner tone="acc">This service is in the past. It won&apos;t appear in upcoming plans. Continue only if you&apos;re back-filling history.</Banner>
                )}
                <Field label="Notes for the team" optional htmlFor="wiz-notes">
                  <Textarea id="wiz-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call time 7:30 AM. Communion this week." />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="flex flex-col gap-4 wp-rise" key="s1">
                <div>
                  <h1 className="text-[26px] font-semibold tracking-[-0.025em]">Who&apos;s serving?</h1>
                  <p className="text-[14.5px] text-t3 mt-1.5">
                    {team?.name} · {date ? longDate(date) : ''}. People are asked to confirm once you publish.
                  </p>
                </div>
                <Field label="Start from" htmlFor="wiz-copy">
                  <Select
                    id="wiz-copy"
                    value={copy}
                    onChange={(v) => setCopy(v as 'last' | 'empty')}
                    options={[
                      { value: 'last', label: 'Copy from the last service', sub: lastPlan ? `${lastPlan.title} · ${shortDate(lastPlan.date)}` : 'No services yet' },
                      { value: 'empty', label: 'Start with an empty team' },
                    ]}
                  />
                </Field>
                <div className="flex flex-col gap-2">
                  {slots.map((s, i) => (
                    <div key={s.id} className="grid gap-2 items-center" style={{ gridTemplateColumns: '22px minmax(0,1fr) minmax(0,1.3fr) 30px' }}>
                      <span className="font-mono text-[12px] text-t4">{i + 1}</span>
                      <Select aria-label={`Position ${i + 1}`} value={s.role} options={POSITIONS.map((p) => ({ value: p, label: p }))} onChange={(v) => setSlots(slots.map((x) => (x.id === s.id ? { ...x, role: v } : x)))} />
                      <Select
                        aria-label={`Person for position ${i + 1}`}
                        value={s.userId}
                        placeholder="Leave unfilled"
                        options={memberOptions(s.id)}
                        onChange={(v) => setSlots(slots.map((x) => (x.id === s.id ? { ...x, userId: v } : x)))}
                      />
                      <button
                        type="button"
                        onClick={() => setSlots(slots.filter((x) => x.id !== s.id))}
                        aria-label={`Remove position ${i + 1}`}
                        className="w-[30px] h-[30px] rounded-full text-t4 hover:bg-acc-bg hover:text-acc flex items-center justify-center"
                      >
                        <X size={15} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {slots.length === 0 && <div className="p-[18px] text-center text-[13.5px] text-t4 rounded-[14px] bg-fill">No positions yet.</div>}
                  <button
                    type="button"
                    onClick={() => setSlots([...slots, { id: nid(), role: 'Lead Vocals', userId: '' }])}
                    className="h-[42px] rounded-[12px] border border-dashed border-line2 text-t2 hover:text-t1 hover:border-t5 text-[13.5px] font-medium"
                  >
                    + Add position
                  </button>
                </div>
                <div className="flex gap-4 text-[13px] text-t3">
                  <span>
                    <span className="font-mono font-bold text-t1">{filled}</span> assigned
                  </span>
                  <span>
                    <span className="font-mono font-bold text-acc">{open}</span> unfilled
                  </span>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4 wp-rise" key="s2">
                <div>
                  <h1 className="text-[26px] font-semibold tracking-[-0.025em]">Build the setlist</h1>
                  <p className="text-[14.5px] text-t3 mt-1.5">Add songs now, or add them later.</p>
                </div>
                <div className="flex items-center gap-3.5 px-4 py-3.5 rounded-[14px] bg-fill">
                  <div className="flex-1">
                    <div className="text-[14px] font-medium">Add songs later</div>
                    <div className="text-[12.5px] text-t4 mt-0.5">The team sees an empty setlist on this service.</div>
                  </div>
                  <Toggle checked={skip} onChange={setSkip} label="Add songs later" />
                </div>
                {!skip && songs.length > 0 && (
                  <ol className="flex flex-col gap-2">
                    {songs.map((s, i) => (
                      <li
                        key={s.songId}
                        className="grid gap-2 items-center px-2.5 py-2 rounded-[14px] border border-line"
                        style={{ gridTemplateColumns: '22px minmax(0,1fr) minmax(0,150px) 76px auto' }}
                      >
                        <span className="font-mono text-[12px] text-t4">{i + 1}</span>
                        <span className="min-w-0">
                          <span className="block text-[14px] font-semibold truncate">{s.title}</span>
                          <span className="block text-[12px] text-t4 truncate">{s.author}</span>
                        </span>
                        <Select size="sm" aria-label={`${s.title} section`} value={s.section} options={SECTIONS.map((v) => ({ value: v, label: v }))} onChange={(v) => setSongs(songs.map((x) => (x.songId === s.songId ? { ...x, section: v } : x)))} />
                        <Select size="sm" aria-label={`${s.title} key`} value={s.key} options={SONG_KEYS.map((k) => ({ value: k, label: k }))} onChange={(v) => setSongs(songs.map((x) => (x.songId === s.songId ? { ...x, key: v } : x)))} />
                        <span className="flex gap-0.5">
                          <button type="button" aria-label={`Move ${s.title} up`} disabled={i === 0} onClick={() => move(i, -1)} className="w-[26px] h-[26px] rounded-[8px] bg-fill text-t2 disabled:opacity-30 flex items-center justify-center">
                            <ArrowUp size={13} aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`Move ${s.title} down`} disabled={i === songs.length - 1} onClick={() => move(i, 1)} className="w-[26px] h-[26px] rounded-[8px] bg-fill text-t2 disabled:opacity-30 flex items-center justify-center">
                            <ArrowDown size={13} aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`Remove ${s.title}`} onClick={() => setSongs(songs.filter((x) => x.songId !== s.songId))} className="w-[26px] h-[26px] rounded-[8px] text-t4 hover:text-acc flex items-center justify-center">
                            <X size={14} aria-hidden="true" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                {!skip && (
                  <div className="rounded-[16px] border border-line p-3">
                    <Input
                      height={40}
                      pill
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search the song library"
                      aria-label="Search the song library"
                      leading={<Search size={15} />}
                      className="!text-[14px] mb-1.5"
                    />
                    {lib.length === 0 && <p className="text-[13px] text-t4 px-1.5 py-3">No songs match.</p>}
                    {lib.map((s) => {
                      const added = songs.some((x) => x.songId === s.id);
                      return (
                        <div key={s.id} className="flex items-center gap-3 px-1.5 py-2 border-b border-line last:border-0">
                          <KeyBadge k={s.defaultKey} size="sm" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13.5px] font-medium truncate">{s.title}</span>
                            <span className="block text-[12px] text-t4 truncate">{s.author}</span>
                          </span>
                          <button
                            type="button"
                            disabled={added}
                            onClick={() =>
                              setSongs([
                                ...songs,
                                {
                                  songId: s.id,
                                  title: s.title,
                                  author: s.author,
                                  key: s.defaultKey || 'C',
                                  section: songs.length === 0 ? 'Call to Worship' : songs.length < 3 ? 'Praise' : 'Worship',
                                  hasLyrics: !!s.lyrics,
                                  youtubeUrl: s.youtubeUrl,
                                },
                              ])
                            }
                            className={cx('h-[30px] px-3 rounded-full text-[12.5px] font-semibold', added ? 'bg-ok-bg text-ok' : 'bg-fill2 text-t1 hover:bg-line2')}
                          >
                            {added ? 'Added' : 'Add'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4 wp-rise" key="s3">
                <div>
                  <h1 className="text-[26px] font-semibold tracking-[-0.025em]">Review and publish</h1>
                  <p className="text-[14.5px] text-t3 mt-1.5">Publishing asks everyone assigned to confirm. Save a draft to keep working on it.</p>
                </div>
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))' }}>
                  <StatTile label="Service" value={name} mono={false} className="[&>div:last-child]:text-[14.5px]" />
                  <StatTile label="Team" value={team?.name ?? ''} mono={false} className="[&>div:last-child]:text-[14.5px]" />
                  <StatTile label="When" value={when ? `${shortDate(when)} · ${timeLabel(time)}` : ''} mono={false} className="[&>div:last-child]:text-[14.5px]" />
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="flex-[1_1_260px] rounded-[16px] border border-line px-4 py-3.5">
                    <div className="text-[13px] font-semibold text-t3 mb-1.5">
                      Team · {filled} assigned{open ? `, ${open} unfilled` : ''}
                    </div>
                    {slots.map((s) => (
                      <div key={s.id} className="flex justify-between gap-2.5 py-1.5 text-[13.5px]">
                        <span className="text-t3">{s.role}</span>
                        <span className={cx('font-medium', s.userId ? 'text-t1' : 'text-acc')}>{s.userId ? memberName(s.userId) : 'Unfilled'}</span>
                      </div>
                    ))}
                    {open > 0 && <p className="text-[12px] text-t4 mt-1.5">Unfilled positions aren&apos;t saved. Add people from the roster later.</p>}
                  </div>
                  <div className="flex-[1_1_260px] rounded-[16px] border border-line px-4 py-3.5">
                    <div className="text-[13px] font-semibold text-t3 mb-1.5">Setlist · {skip || !songs.length ? 'Songs will be added later' : `${songs.length} songs`}</div>
                    {!skip &&
                      songs.map((s, i) => (
                        <div key={s.songId} className="flex gap-2.5 py-1.5 text-[13.5px]">
                          <span className="font-mono text-t4 w-3.5">{i + 1}</span>
                          <span className="flex-1 font-medium">{s.title}</span>
                          <span className="font-mono text-acc font-bold">{s.key}</span>
                        </div>
                      ))}
                  </div>
                </div>
                {noLyrics.length > 0 && policy !== 'off' && (
                  <Banner tone="amb">
                    {noLyrics.join(', ')} {noLyrics.length === 1 ? 'has' : 'have'} no lyrics.{' '}
                    {policy === 'block'
                      ? 'Your church settings block publishing until lyrics are added.'
                      : `The lyrics email will skip ${noLyrics.length === 1 ? 'it' : 'them'}.`}
                  </Banner>
                )}
                {redesignFlags.wizardLyricsToggle ? null : (
                  <p className="text-[12.5px] text-t4">
                    Lyrics email:{' '}
                    {church?.setlistSettings?.sendLyricsOnPublish && church.setlistSettings.lyricsRecipientEmail
                      ? `sent to ${church.setlistSettings.lyricsRecipientEmail} when the setlist is published.`
                      : 'off. Turn it on in Church settings.'}
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2.5 mt-[26px] pt-5 border-t border-line">
              {step > 0 && (
                <Button variant="ghost" shape="form" size="lg" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              <Button variant="plain" size="lg" onClick={() => navigate('/admin/roster')}>
                Cancel
              </Button>
              <span className="flex-1" />
              {step === 3 ? (
                <>
                  <Button variant="ghost" shape="form" size="lg" onClick={() => create(false)} loading={submitting === 'draft'} disabled={!!submitting}>
                    Save draft
                  </Button>
                  <Button shape="form" size="lg" onClick={() => create(true)} loading={submitting === 'publish'} disabled={!!submitting || blocked}>
                    Publish
                  </Button>
                </>
              ) : (
                <Button shape="form" size="lg" onClick={next}>
                  {step === 2 && !songs.length ? 'Skip setlist for now' : step === 2 ? 'Review' : 'Continue'}
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      <PlanLimitModal
        isOpen={!!limit}
        onClose={() => setLimit(null)}
        message={limit?.message ?? ''}
        maxPlans={limit?.maxPlans ?? 0}
        tier={limit?.tier ?? ''}
        plans={allPlans}
        onDeletePlan={async (planId) => {
          if (!churchId) return;
          await plansApi.delete(churchId, planId);
          setAllPlans((prev) => prev.filter((p) => p.id !== planId));
        }}
        onRetry={() => void create(limit?.publish ?? false)}
        onUpgrade={() => {
          setLimit(null);
          navigate('/admin/subscription');
        }}
      />
    </div>
  );
}
